import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '../../api/axiosInstance'
import Swal from 'sweetalert2'
import { baseurImage } from '../../Baseurl'
import dayjs from 'dayjs'

const C = { gray: '#5B6B79', blue: '#96C5F7', green: '#2CA87F', red: '#9E2B25', orange: '#F29E4C' }
const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const fmt = (t) => {
  if (!t) return ''
  const [h, m] = t.split(':')
  const hour = parseInt(h, 10)
  return `${hour % 12 === 0 ? 12 : hour % 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`
}

const IBtn = ({ icon, color, title, onClick, size = 24 }) => (
  <button onClick={onClick} title={title} style={{
    width: size, height: size, borderRadius: 6, border: 'none', cursor: 'pointer',
    background: `${color}22`, color, display: 'inline-flex', alignItems: 'center',
    justifyContent: 'center', fontSize: size - 10, flexShrink: 0,
  }}
    onMouseEnter={e => e.currentTarget.style.background = color + '44'}
    onMouseLeave={e => e.currentTarget.style.background = color + '22'}>
    <i className={`ti ${icon}`} />
  </button>
)

export default function Schedule() {
  const navigate = useNavigate()
  const today = dayjs()

  // ── Doctor state ──────────────────────────────────────────────────────────
  const [doctors, setDoctors] = useState([])
  const [doctorSearch, setDoctorSearch] = useState('')
  const [selectedDoctor, setSelectedDoctor] = useState(null)

  // ── Calendar state ────────────────────────────────────────────────────────
  const [currentMonth, setCurrentMonth] = useState(dayjs().startOf('month'))
  const [scheduleData, setScheduleData] = useState([])
  const [loadingSchedule, setLoadingSchedule] = useState(false)

  // ── Day modal ─────────────────────────────────────────────────────────────
  const [selectedDate, setSelectedDate] = useState(null)
  const [dayModalOpen, setDayModalOpen] = useState(false)

  // ── Shift form modal ──────────────────────────────────────────────────────
  const [shiftFormOpen, setShiftFormOpen] = useState(false)
  const [editingShift, setEditingShift] = useState(null)
  const [shiftForm, setShiftForm] = useState({ start_time: '08:00', end_time: '16:00', note: '' })
  const [savingShift, setSavingShift] = useState(false)

  // ── Copy modal ────────────────────────────────────────────────────────────
  const [copyModalOpen, setCopyModalOpen] = useState(false)
  const [copyTargets, setCopyTargets] = useState([])
  const [copying, setCopying] = useState(false)

  // ── Clinic working hours (global, drives the Appointments calendar grid) ──
  const [workingHoursModalOpen, setWorkingHoursModalOpen] = useState(false)
  const [workingHours, setWorkingHours] = useState({ start_time: '07:00', end_time: '20:00' })
  const [workingHoursForm, setWorkingHoursForm] = useState({ start_time: '07:00', end_time: '20:00' })
  const [savingWorkingHours, setSavingWorkingHours] = useState(false)

  useEffect(() => {
    axiosInstance.get('getClinicWorkingHours')
      .then(res => {
        if (res.data?.success && res.data.data) {
          const wh = {
            start_time: (res.data.data.start_time || '07:00:00').slice(0, 5),
            end_time: (res.data.data.end_time || '20:00:00').slice(0, 5),
          }
          setWorkingHours(wh)
          setWorkingHoursForm(wh)
        }
      })
      .catch(err => console.error('Failed to load working hours', err))
  }, [])

  const openWorkingHoursModal = () => {
    setWorkingHoursForm(workingHours)
    setWorkingHoursModalOpen(true)
  }

  const handleSaveWorkingHours = async () => {
    const { start_time, end_time } = workingHoursForm
    if (!start_time || !end_time) return
    if (start_time >= end_time) {
      Swal.fire('Invalid Time', 'End time must be after start time', 'warning')
      return
    }
    setSavingWorkingHours(true)
    try {
      await axiosInstance.put('updateClinicWorkingHours', { start_time, end_time })
      setWorkingHours({ start_time, end_time })
      setWorkingHoursModalOpen(false)
      Swal.fire({ icon: 'success', title: 'Working Hours Updated', timer: 1400, showConfirmButton: false })
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save working hours', 'error')
    } finally {
      setSavingWorkingHours(false)
    }
  }

  // ── Fetch doctors ─────────────────────────────────────────────────────────
  useEffect(() => {
    axiosInstance.get('getAllDoctors')
      .then(res => { if (res.data.success) setDoctors(res.data.data) })
      .catch(err => console.error('Failed to load doctors', err))
  }, [])

  // ── Fetch schedule on doctor/month change ─────────────────────────────────
  useEffect(() => {
    if (selectedDoctor) fetchSchedule()
    else setScheduleData([])
  }, [selectedDoctor, currentMonth])

  const fetchSchedule = () => {
    setLoadingSchedule(true)
    axiosInstance.get(`getDoctorDateSchedule/${selectedDoctor.id}`, {
      params: { year: currentMonth.year(), month: currentMonth.month() + 1 },
    })
      .then(res => setScheduleData(res.data.success ? res.data.data : []))
      .catch(() => setScheduleData([]))
      .finally(() => setLoadingSchedule(false))
  }

  // ── Group by date string ──────────────────────────────────────────────────
  const scheduleByDate = useMemo(() => {
    const map = {}
    scheduleData.forEach(row => {
      const d = (row.schedule_date || '').slice(0, 10)
      if (!map[d]) map[d] = []
      map[d].push(row)
    })
    return map
  }, [scheduleData])

  // ── Calendar days (Mon-start grid) ────────────────────────────────────────
  const calendarDays = useMemo(() => {
    const start = currentMonth.startOf('month')
    const offset = start.day() === 0 ? 6 : start.day() - 1
    const days = []
    for (let i = 0; i < offset; i++) days.push(null)
    for (let d = 1; d <= currentMonth.daysInMonth(); d++) days.push(currentMonth.date(d))
    return days
  }, [currentMonth])

  // ── Day modal helpers ─────────────────────────────────────────────────────
  const getShiftsForDate = (dateObj) =>
    dateObj ? (scheduleByDate[dateObj.format('YYYY-MM-DD')] || []) : []

  const selectedShifts = useMemo(() => getShiftsForDate(selectedDate), [selectedDate, scheduleByDate])
  const dayOffEntry = selectedShifts.find(s => s.is_day_off)
  const regularShifts = selectedShifts.filter(s => !s.is_day_off)

  const openDayModal = (dateObj) => {
    setSelectedDate(dateObj)
    setDayModalOpen(true)
  }

  // ── Shift form ────────────────────────────────────────────────────────────
  const openAddShift = () => {
    setShiftForm({ start_time: '08:00', end_time: '16:00', note: '' })
    setEditingShift(null)
    setShiftFormOpen(true)
  }

  const openEditShift = (shift) => {
    setShiftForm({
      start_time: (shift.start_time || '08:00').slice(0, 5),
      end_time: (shift.end_time || '16:00').slice(0, 5),
      note: shift.note || '',
    })
    setEditingShift(shift)
    setShiftFormOpen(true)
  }

  const handleSaveShift = async () => {
    const { start_time, end_time, note } = shiftForm
    if (!start_time || !end_time) return
    if (start_time >= end_time) {
      Swal.fire('Invalid Time', 'End time must be after start time', 'warning')
      return
    }
    setSavingShift(true)
    try {
      if (editingShift) {
        await axiosInstance.put(`updateDoctorDateSchedule/${editingShift.id}`, { start_time, end_time, note })
      } else {
        await axiosInstance.post('addDoctorDateSchedule', {
          doctor_id: selectedDoctor.id,
          schedule_date: selectedDate.format('YYYY-MM-DD'),
          start_time, end_time, note, is_day_off: 0,
        })
      }
      setShiftFormOpen(false)
      fetchSchedule()
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save shift', 'error')
    } finally {
      setSavingShift(false)
    }
  }

  const handleMarkDayOff = async () => {
    const { value: note } = await Swal.fire({
      title: 'Mark as Day Off',
      input: 'text',
      inputLabel: 'Reason (optional)',
      inputPlaceholder: 'e.g. Out of country, Conference...',
      showCancelButton: true,
      confirmButtonText: 'Mark Off',
      confirmButtonColor: C.red,
    })
    if (note === undefined) return // cancelled
    try {
      await axiosInstance.post('addDoctorDateSchedule', {
        doctor_id: selectedDoctor.id,
        schedule_date: selectedDate.format('YYYY-MM-DD'),
        is_day_off: 1,
        note: note || null,
      })
      fetchSchedule()
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed', 'error')
    }
  }

  const handleDeleteShift = async (id) => {
    const result = await Swal.fire({
      title: 'Delete this entry?', icon: 'warning', showCancelButton: true,
      confirmButtonColor: C.red, confirmButtonText: 'Delete',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.delete(`deleteDoctorDateSchedule/${id}`)
      fetchSchedule()
    } catch {
      Swal.fire('Error', 'Failed to delete', 'error')
    }
  }

  // ── Copy ──────────────────────────────────────────────────────────────────
  const openCopyModal = () => { setCopyTargets([]); setCopyModalOpen(true) }

  const toggleCopyDate = (dateStr) =>
    setCopyTargets(prev => prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr])

  const handleCopy = async () => {
    if (!copyTargets.length) return
    setCopying(true)
    try {
      await axiosInstance.post('copyDoctorDateSchedule', {
        doctor_id: selectedDoctor.id,
        source_date: selectedDate.format('YYYY-MM-DD'),
        target_dates: copyTargets,
      })
      setCopyModalOpen(false)
      setCopyTargets([])
      fetchSchedule()
      Swal.fire('Copied!', `Schedule copied to ${copyTargets.length} date(s)`, 'success')
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Copy failed', 'error')
    } finally {
      setCopying(false)
    }
  }

  // ── Calendar cell status ──────────────────────────────────────────────────
  const getCellStatus = (dateObj) => {
    if (!dateObj) return null
    const shifts = scheduleByDate[dateObj.format('YYYY-MM-DD')] || []
    if (!shifts.length) return 'empty'
    if (shifts.some(s => s.is_day_off)) return 'off'
    return 'scheduled'
  }

  const filteredDoctors = doctors.filter(d =>
    (d.displayName || d.fullName || '').toLowerCase().includes(doctorSearch.toLowerCase())
  )

  // ── Copy modal: calendar days for current month ───────────────────────────
  const copyCalDays = useMemo(() => {
    const start = currentMonth.startOf('month')
    const offset = start.day() === 0 ? 6 : start.day() - 1
    const days = []
    for (let i = 0; i < offset; i++) days.push(null)
    for (let d = 1; d <= currentMonth.daysInMonth(); d++) days.push(currentMonth.date(d))
    return days
  }, [currentMonth])

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => navigate(-1)} style={{
              width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0',
              background: '#f8f9fb', color: C.gray, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0,
            }}
              onMouseEnter={e => { e.currentTarget.style.background = C.blue + '22'; e.currentTarget.style.color = C.blue }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = C.gray }}>
              <i className="ti ti-arrow-left" />
            </button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${C.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-calendar-time" style={{ color: C.blue, fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Doctor Schedules</div>
              <div style={{ fontSize: 11, color: C.gray }}>Per-day availability — set shifts or mark days off</div>
            </div>

            {/* Legend */}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 14, alignItems: 'center' }}>
              {[['#e8f5e9', C.green, 'Scheduled'], [`${C.red}18`, C.red, 'Day Off'], ['#f5f7fa', C.gray, 'No Schedule']].map(([bg, col, label]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.gray }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3, background: bg, border: `1px solid ${col}40` }} />
                  {label}
                </div>
              ))}
              <button onClick={openWorkingHoursModal} title="Set clinic-wide working hours shown on the Appointments calendar"
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.blue}`, background: '#fff', color: C.blue, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = `${C.blue}15`}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                <i className="ti ti-clock-hour-4" />
                Working Hours: {fmt(workingHours.start_time)} – {fmt(workingHours.end_time)}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', height: 'calc(100vh - 178px)', minHeight: 520 }}>

            {/* ── Left: Doctor List ───────────────────────────────────────── */}
            <div style={{ width: 230, flexShrink: 0, borderRight: '1px solid #e8ecf0', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '10px 12px', borderBottom: '1px solid #f0f2f5' }}>
                <div style={{ position: 'relative' }}>
                  <i className="ti ti-search" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: C.gray, fontSize: 13, pointerEvents: 'none' }} />
                  <input value={doctorSearch} onChange={e => setDoctorSearch(e.target.value)} placeholder="Search doctors..."
                    style={{ width: '100%', paddingLeft: 28, paddingRight: 8, height: 32, fontSize: 12, border: '1px solid #dde3eb', borderRadius: 7, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {filteredDoctors.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: C.gray, fontSize: 12 }}>No doctors found</div>}
                {filteredDoctors.map(doc => {
                  const active = selectedDoctor?.id === doc.id
                  return (
                    <div key={doc.id} onClick={() => setSelectedDoctor(doc)} style={{
                      display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px',
                      cursor: 'pointer', borderBottom: '1px solid #f5f7fa',
                      background: active ? `${C.blue}18` : '#fff',
                      borderLeft: `3px solid ${active ? C.blue : 'transparent'}`,
                    }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f8f9fb' }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.background = active ? `${C.blue}18` : '#fff' }}>
                      <img
                        src={doc.personalPhoto && doc.personalPhoto !== 'null' ? `${baseurImage}${doc.personalPhoto}` : '../assets/images/user/avatar-1.jpg'}
                        onError={e => { e.target.src = '../assets/images/user/avatar-1.jpg' }}
                        alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${active ? C.blue : '#e8ecf0'}`, flexShrink: 0 }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: active ? C.blue : '#1a202c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {doc.displayName || doc.fullName || '—'}
                        </div>
                        {doc.specialty && <div style={{ fontSize: 10, color: C.gray, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.specialty}</div>}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* ── Right: Calendar ─────────────────────────────────────────── */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {!selectedDoctor ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: C.gray }}>
                  <i className="ti ti-user-question" style={{ fontSize: 40, marginBottom: 10, opacity: 0.3 }} />
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Select a doctor to view their schedule</div>
                </div>
              ) : (
                <>
                  {/* Month navigation */}
                  <div style={{ padding: '12px 20px', borderBottom: '1px solid #f0f2f5', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button onClick={() => setCurrentMonth(m => m.subtract(1, 'month'))} style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.gray, fontSize: 14 }}>
                      <i className="ti ti-chevron-left" />
                    </button>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1a202c', minWidth: 130, textAlign: 'center' }}>
                      {currentMonth.format('MMMM YYYY')}
                    </span>
                    <button onClick={() => setCurrentMonth(m => m.add(1, 'month'))} style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.gray, fontSize: 14 }}>
                      <i className="ti ti-chevron-right" />
                    </button>
                    <button onClick={() => setCurrentMonth(dayjs().startOf('month'))} style={{ marginLeft: 4, padding: '4px 10px', borderRadius: 7, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: C.gray }}>
                      Today
                    </button>
                    {loadingSchedule && <span style={{ fontSize: 11, color: C.gray, marginLeft: 8 }}><i className="ti ti-loader-2" /> Loading...</span>}
                  </div>

                  {/* Calendar grid */}
                  <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
                    {/* Day headers */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, paddingTop: 12, marginBottom: 4 }}>
                      {DAY_HEADERS.map(d => (
                        <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: C.gray, textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: 6 }}>{d}</div>
                      ))}
                    </div>

                    {/* Date cells */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                      {calendarDays.map((dateObj, idx) => {
                        if (!dateObj) return <div key={`empty-${idx}`} />
                        const dateStr = dateObj.format('YYYY-MM-DD')
                        const isToday = dateObj.isSame(today, 'day')
                        const status = getCellStatus(dateObj)
                        const shifts = scheduleByDate[dateStr] || []
                        const isDayOff = shifts.some(s => s.is_day_off)
                        const dayOffNote = shifts.find(s => s.is_day_off)?.note
                        const workShifts = shifts.filter(s => !s.is_day_off)

                        let cellBg = '#fff'
                        let cellBorder = '#e8ecf0'
                        if (isDayOff) { cellBg = `${C.red}0d`; cellBorder = `${C.red}40` }
                        else if (workShifts.length > 0) { cellBg = '#e8f5e9'; cellBorder = `${C.green}50` }

                        return (
                          <div key={dateStr} onClick={() => openDayModal(dateObj)}
                            style={{
                              minHeight: 80, border: `1px solid ${isToday ? C.blue : cellBorder}`,
                              borderRadius: 8, padding: '6px 8px', cursor: 'pointer',
                              background: cellBg,
                              boxShadow: isToday ? `0 0 0 2px ${C.blue}40` : 'none',
                              transition: 'box-shadow 0.12s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.boxShadow = `0 2px 8px rgba(0,0,0,0.1)`}
                            onMouseLeave={e => e.currentTarget.style.boxShadow = isToday ? `0 0 0 2px ${C.blue}40` : 'none'}>

                            {/* Date number */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                              <span style={{
                                fontSize: 12, fontWeight: 700,
                                color: isToday ? '#fff' : isDayOff ? C.red : '#1a202c',
                                background: isToday ? C.blue : 'transparent',
                                borderRadius: isToday ? '50%' : 0,
                                width: isToday ? 20 : 'auto', height: isToday ? 20 : 'auto',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                {dateObj.date()}
                              </span>
                              {isDayOff && <i className="ti ti-plane" style={{ fontSize: 11, color: C.red }} title="Day Off" />}
                              {!isDayOff && workShifts.length > 0 && (
                                <span style={{ fontSize: 10, fontWeight: 700, color: C.green }}>{workShifts.length} shift{workShifts.length > 1 ? 's' : ''}</span>
                              )}
                            </div>

                            {/* Shifts preview */}
                            {isDayOff ? (
                              <div style={{ fontSize: 10, color: C.red, fontWeight: 600 }}>Day Off{dayOffNote ? ` — ${dayOffNote}` : ''}</div>
                            ) : (
                              workShifts.slice(0, 2).map(s => (
                                <div key={s.id} style={{ fontSize: 10, color: C.green, fontWeight: 600, lineHeight: 1.4 }}>
                                  {fmt(s.start_time)} – {fmt(s.end_time)}
                                </div>
                              ))
                            )}
                            {workShifts.length > 2 && <div style={{ fontSize: 10, color: C.gray }}>+{workShifts.length - 2} more</div>}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Day Detail Modal ──────────────────────────────────────────────────── */}
      {dayModalOpen && selectedDate && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 420, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>

            {/* Modal header */}
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: `${C.blue}20`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: C.blue, lineHeight: 1 }}>{selectedDate.format('MMM').toUpperCase()}</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: C.blue, lineHeight: 1 }}>{selectedDate.date()}</span>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c' }}>{selectedDate.format('dddd, MMMM D YYYY')}</div>
                <div style={{ fontSize: 11, color: C.gray }}>{selectedDoctor?.displayName || selectedDoctor?.fullName}</div>
              </div>
              <button onClick={() => setDayModalOpen(false)} style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.gray, fontSize: 14 }}>
                <i className="ti ti-x" />
              </button>
            </div>

            {/* Modal body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>

              {/* Day off entry */}
              {dayOffEntry && (
                <div style={{ background: `${C.red}0d`, border: `1px solid ${C.red}30`, borderRadius: 8, padding: '10px 12px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <i className="ti ti-plane" style={{ color: C.red, fontSize: 16 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.red }}>Day Off</div>
                    {dayOffEntry.note && <div style={{ fontSize: 11, color: C.gray }}>{dayOffEntry.note}</div>}
                  </div>
                  <IBtn icon="ti-trash" color={C.red} title="Remove day off" onClick={() => handleDeleteShift(dayOffEntry.id)} />
                </div>
              )}

              {/* Regular shifts */}
              {regularShifts.length === 0 && !dayOffEntry && (
                <div style={{ textAlign: 'center', color: C.gray, fontSize: 12, padding: '12px 0' }}>No shifts scheduled for this day.</div>
              )}
              {regularShifts.map(shift => (
                <div key={shift.id} style={{ background: '#e8f5e9', border: `1px solid ${C.green}30`, borderRadius: 8, padding: '10px 12px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <i className="ti ti-clock" style={{ color: C.green, fontSize: 16, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>{fmt(shift.start_time)} – {fmt(shift.end_time)}</div>
                    {shift.note && <div style={{ fontSize: 11, color: C.gray, marginTop: 2 }}>{shift.note}</div>}
                  </div>
                  <IBtn icon="ti-edit" color={C.orange} title="Edit shift" onClick={() => openEditShift(shift)} />
                  <IBtn icon="ti-trash" color={C.red} title="Delete shift" onClick={() => handleDeleteShift(shift.id)} />
                </div>
              ))}
            </div>

            {/* Modal footer */}
            <div style={{ padding: '10px 18px 14px', borderTop: '1px solid #e8ecf0', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {!dayOffEntry && (
                <>
                  <button onClick={openAddShift} style={{ flex: 1, height: 34, borderRadius: 8, border: 'none', background: C.blue, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                    <i className="ti ti-plus" /> Add Shift
                  </button>
                  <button onClick={handleMarkDayOff} style={{ flex: 1, height: 34, borderRadius: 8, border: `1px solid ${C.red}`, background: '#fff', color: C.red, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                    <i className="ti ti-plane" /> Mark Day Off
                  </button>
                </>
              )}
              {regularShifts.length > 0 && (
                <button onClick={openCopyModal} style={{ flex: 1, height: 34, borderRadius: 8, border: `1px solid ${C.orange}`, background: '#fff', color: C.orange, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  <i className="ti ti-copy" /> Copy to Dates
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Shift Form Modal ──────────────────────────────────────────────────── */}
      {shiftFormOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 340, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: `${C.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="ti ti-clock" style={{ color: C.blue, fontSize: 14 }} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#1a202c' }}>{editingShift ? 'Edit Shift' : 'Add Shift'}</span>
              <span style={{ fontSize: 11, color: C.gray, marginLeft: 4 }}>{selectedDate?.format('MMM D')}</span>
              <button onClick={() => setShiftFormOpen(false)} style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.gray, fontSize: 14 }}>
                <i className="ti ti-x" />
              </button>
            </div>
            <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[['Start Time', 'start_time'], ['End Time', 'end_time']].map(([label, key]) => (
                <div key={key}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: C.gray, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 5 }}>{label}</label>
                  <input type="time" value={shiftForm[key]} onChange={e => setShiftForm(f => ({ ...f, [key]: e.target.value }))}
                    style={{ width: '100%', height: 36, border: '1px solid #dde3eb', borderRadius: 8, padding: '0 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: C.gray, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 5 }}>Note (optional)</label>
                <input type="text" value={shiftForm.note} onChange={e => setShiftForm(f => ({ ...f, note: e.target.value }))}
                  placeholder="e.g. Morning rounds, Clinic only..."
                  style={{ width: '100%', height: 36, border: '1px solid #dde3eb', borderRadius: 8, padding: '0 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ padding: '8px 18px 16px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShiftFormOpen(false)} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: C.gray, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={handleSaveShift} disabled={savingShift} style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: C.blue, color: '#fff', fontSize: 12, cursor: savingShift ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: savingShift ? 0.7 : 1 }}>
                {savingShift ? 'Saving...' : 'Save Shift'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Copy to Dates Modal ───────────────────────────────────────────────── */}
      {copyModalOpen && selectedDate && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 380, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: `${C.orange}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="ti ti-copy" style={{ color: C.orange, fontSize: 14 }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c' }}>Copy to Dates</div>
                <div style={{ fontSize: 11, color: C.gray }}>From {selectedDate.format('MMM D')} — click dates to select</div>
              </div>
              <button onClick={() => setCopyModalOpen(false)} style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.gray, fontSize: 14 }}>
                <i className="ti ti-x" />
              </button>
            </div>
            <div style={{ padding: '12px 18px' }}>
              {/* Mini calendar for copy target selection */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 6 }}>
                {DAY_HEADERS.map(d => <div key={d} style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, color: C.gray, padding: '2px 0' }}>{d}</div>)}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
                {copyCalDays.map((dateObj, idx) => {
                  if (!dateObj) return <div key={`ce-${idx}`} />
                  const dateStr = dateObj.format('YYYY-MM-DD')
                  const isSource = dateStr === selectedDate.format('YYYY-MM-DD')
                  const isSelected = copyTargets.includes(dateStr)
                  const isToday = dateObj.isSame(today, 'day')
                  return (
                    <div key={dateStr} onClick={() => !isSource && toggleCopyDate(dateStr)}
                      style={{
                        height: 30, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: isSelected ? 700 : 400, cursor: isSource ? 'default' : 'pointer',
                        background: isSource ? '#f0f2f5' : isSelected ? C.blue : isToday ? `${C.blue}20` : '#fff',
                        color: isSource ? C.gray : isSelected ? '#fff' : isToday ? C.blue : '#1a202c',
                        border: `1px solid ${isSource ? '#e8ecf0' : isSelected ? C.blue : isToday ? C.blue : '#e8ecf0'}`,
                        opacity: isSource ? 0.5 : 1,
                      }}>
                      {dateObj.date()}
                    </div>
                  )
                })}
              </div>
              {copyTargets.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 11, color: C.gray }}>
                  {copyTargets.length} date{copyTargets.length !== 1 ? 's' : ''} selected
                </div>
              )}
            </div>
            <div style={{ padding: '8px 18px 14px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setCopyModalOpen(false)} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: C.gray, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={handleCopy} disabled={!copyTargets.length || copying} style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: copyTargets.length ? C.orange : '#ccc', color: '#fff', fontSize: 12, cursor: (!copyTargets.length || copying) ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: copying ? 0.7 : 1 }}>
                {copying ? 'Copying...' : `Copy to ${copyTargets.length || 0} date${copyTargets.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Clinic Working Hours Modal ────────────────────────────────────────── */}
      {workingHoursModalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 340, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: `${C.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="ti ti-clock-hour-4" style={{ color: C.blue, fontSize: 14 }} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#1a202c' }}>Clinic Working Hours</span>
              <button onClick={() => setWorkingHoursModalOpen(false)} style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.gray, fontSize: 14 }}>
                <i className="ti ti-x" />
              </button>
            </div>
            <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 11, color: C.gray }}>
                This is the clinic-wide window shown on the Appointments calendar view. It applies to every day and every doctor.
              </div>
              {[['Start Time', 'start_time'], ['End Time', 'end_time']].map(([label, key]) => (
                <div key={key}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: C.gray, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 5 }}>{label}</label>
                  <input type="time" value={workingHoursForm[key]} onChange={e => setWorkingHoursForm(f => ({ ...f, [key]: e.target.value }))}
                    style={{ width: '100%', height: 36, border: '1px solid #dde3eb', borderRadius: 8, padding: '0 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
            </div>
            <div style={{ padding: '8px 18px 16px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setWorkingHoursModalOpen(false)} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: C.gray, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={handleSaveWorkingHours} disabled={savingWorkingHours} style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: C.blue, color: '#fff', fontSize: 12, cursor: savingWorkingHours ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: savingWorkingHours ? 0.7 : 1 }}>
                {savingWorkingHours ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
